import {
  SalesforceConfig,
  SalesforceSettingsMeta,
} from 'module/api/integrations/salesforce/types';
import { EmailUser } from 'module/api/integrations/salesforce/types';
import { UserStatus } from 'module/api/user/types';
import { userOption } from 'module/helpers/field/user-option';
import { getRandomColor } from 'module/helpers/get-random-color';
import {
  useGetAllActiveNativeUsers,
  useGetAllNativeActiveUsersMap,
} from 'module/hooks/use-member';
import { useTranslate, useTranslation } from 'module/hooks/use-translation';
import DeactivatedUser from 'module/ui/assets/svgs/deactivated_user.svg';
import { Input } from 'module/ui/components/atoms/input/input';
import { MaskableContent } from 'module/ui/components/atoms/maskable-content';
import { Switch } from 'module/ui/components/atoms/switch/switch';
import { Tag } from 'module/ui/components/atoms/tag';
import { Label01 } from 'module/ui/components/styles';
import { isValidEmail } from 'module/ui/utils/is-valid-email';
import { Button, CreatableSelect, type UserOption } from '@rocketlane/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Space } from 'antd';
import {
  SALESFORCE_CONFIG_QUERY_KEY,
  useUpdateSalesforceSettingsMutation,
} from 'queries/salesforce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

const Container = styled.div`
  margin-left: var(--spacing-07);
  display: flex;
  flex-direction: column;
`;

const SecondaryText = styled.div`
  ${Label01};
  color: var(--gray-70);
`;

const FlexWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

const SelectWrapper = styled.div`
  display: flex;
`;

const MIN = 5;
const WIDTH = '500px';

interface EmailRecipientOption extends UserOption {
  extra?: {
    id?: number;
  };
  isNew?: boolean;
}

const getUsersInBEFormat = (usr: EmailRecipientOption[]) => {
  return usr.reduce((acc, x) => {
    if (x.extra?.id) {
      acc.push({
        userId: x.extra?.id,
      });
    } else if (x.isNew) {
      acc.push({
        emailId: x.value as string,
      });
    }

    return acc;
  }, [] as EmailUser[]);
};

export const SalesforceEmailSettings = ({
  integrationId,
  config,
}: {
  integrationId?: number;
  config?: Pick<SalesforceConfig, 'settingsMeta'>;
}) => {
  const settingsMeta = config?.settingsMeta;
  const translate = useTranslate();
  const usersMap = useGetAllNativeActiveUsersMap();
  const users = useGetAllActiveNativeUsers();
  const userLabel = useTranslation('users');
  const [userInput, setUserInput] = useState('');
  const { mutateAsync: updateSettings } = useUpdateSalesforceSettingsMutation();
  const queryClient = useQueryClient();
  const [selectedUsers, setSelectedUsers] = useState<EmailRecipientOption[]>(
    [],
  );

  const [timeDuration, setTimeDuration] = useState(
    settingsMeta?.errorEmailIntervalSeconds
      ? settingsMeta.errorEmailIntervalSeconds / 60
      : undefined,
  );
  const timeDurationRef = useRef(timeDuration);

  const userOptions = useMemo(
    () => [
      {
        id: 'my_team',
        label: userLabel,
        options: users.map((user): EmailRecipientOption => {
          const option = userOption(user);
          const userStatus = option.extra?.userStatus;

          return {
            badgeIcon: option.logoUrl ? (
              <img
                alt="company-logo"
                className="h-full w-full object-cover"
                src={`${option.logoUrl}?v2`}
              />
            ) : undefined,
            color: option.color,
            description: option.description,
            extra: {
              id: option.extra?.id,
            },
            imageUrl:
              userStatus === UserStatus.Inactive
                ? DeactivatedUser
                : option.imageUrl,
            label: option.label,
            value: option.value,
          };
        }),
      },
    ],
    [users, userLabel],
  );

  const onUpdate = useCallback(
    (payload: SalesforceSettingsMeta) => {
      if (integrationId) {
        updateSettings({ integrationId, payload }).then((resp) => {
          queryClient.setQueryData(
            [SALESFORCE_CONFIG_QUERY_KEY, integrationId],
            {
              ...config,
              settingsMeta: resp,
            },
          );
          setSelectedUsers([]);
        });
      }
    },
    [config, integrationId, queryClient, updateSettings],
  );

  const resettingDurationIfValueIncorrect = useCallback(() => {
    if ((timeDuration ?? 0) < MIN) {
      setTimeDuration(timeDurationRef.current);
    } else {
      timeDurationRef.current = timeDuration;
      if (config?.settingsMeta) {
        onUpdate({
          ...config?.settingsMeta,
          errorEmailIntervalSeconds: (timeDuration ?? 0) * 60,
        });
      }
    }
  }, [config, onUpdate, timeDuration]);

  const handleInputChange = useCallback((input, action) => {
    if (action.action !== 'input-blur' && action.action !== 'menu-close') {
      if (input != null) {
        setUserInput(input.trim().toLowerCase());
      }
    }
  }, []);

  const handleAddEmailFlow = useCallback((emailId: string) => {
    if (isValidEmail(emailId)) {
      setSelectedUsers((prev) => [
        ...prev,
        {
          label: emailId,
          value: emailId,
          isNew: true,
        },
      ]);
      setUserInput('');
    }
  }, []);

  useEffect(() => {
    if (config?.settingsMeta && settingsMeta?.errorEmailRecipients) {
      settingsMeta.errorEmailRecipients.reduce((acc, x) => {
        const xBeforeAssigningEmail = { ...x };

        if (x.userId != null) {
          const email = usersMap[x.userId]?.emailId;

          if (email != null) {
            x.emailId = email;
          }
        }
        if (x.emailId == null) {
          onUpdate({
            ...config?.settingsMeta,
            errorEmailRecipients:
              config?.settingsMeta.errorEmailRecipients?.filter((y) =>
                xBeforeAssigningEmail.emailId
                  ? y.emailId !== x.emailId
                  : y.userId !== x.userId,
              ),
          });
        }

        return acc;
      }, [] as any[]);
    }
  }, [
    config?.settingsMeta,
    onUpdate,
    settingsMeta?.errorEmailRecipients,
    usersMap,
  ]);

  if (settingsMeta == null) {
    return <></>;
  }

  return (
    <MaskableContent showMask={!integrationId}>
      <Container>
        <SecondaryText>
          <FormattedMessage id="settings.integrations.sf.enable_error_email.title" />
        </SecondaryText>
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
          <FlexWrapper
            style={{
              marginTop: 'var(--spacing-05)',
            }}
          >
            <Switch
              checked={settingsMeta.errorEmailEnabled}
              onChange={(checked) => {
                if (config?.settingsMeta) {
                  onUpdate({
                    ...config?.settingsMeta,
                    errorEmailEnabled: checked,
                    ...(!settingsMeta.errorEmailIntervalSeconds
                      ? {
                          errorEmailIntervalSeconds: 600,
                        }
                      : {}),
                  });
                }
              }}
              size="small"
            />
            <div>
              {translate('settings.integrations.sf.enable_error_email.switch')}
            </div>
          </FlexWrapper>
          {settingsMeta.errorEmailEnabled && (
            <>
              {!!settingsMeta.errorEmailRecipients &&
                settingsMeta.errorEmailRecipients.length > 0 && (
                  <div>
                    <SecondaryText>
                      <FormattedMessage id="settings.integrations.sf.enable_error_email.emails.title" />
                    </SecondaryText>
                    <FlexWrapper
                      style={{
                        marginTop: 'var(--spacing-03)',
                      }}
                    >
                      {settingsMeta.errorEmailRecipients.reduce(
                        (acc, curr, index) => {
                          const currCopy = { ...curr };

                          if (curr.userId != null) {
                            const email = usersMap[curr.userId]?.emailId;

                            if (email != null) {
                              curr.emailId = email;
                            }
                          }
                          if (curr.emailId != null) {
                            acc.push(
                              <Tag
                                key={index}
                                color={
                                  curr.emailId
                                    ? getRandomColor(curr.emailId)
                                    : 'gray'
                                }
                                closable
                                onClose={() => {
                                  if (config?.settingsMeta) {
                                    onUpdate({
                                      ...config?.settingsMeta,
                                      errorEmailRecipients:
                                        config?.settingsMeta.errorEmailRecipients?.filter(
                                          (y) =>
                                            currCopy.emailId
                                              ? y.emailId !== currCopy.emailId
                                              : y.userId !== currCopy.userId,
                                        ),
                                    });
                                  }
                                }}
                              >
                                {curr.emailId}
                              </Tag>,
                            );
                          }

                          return acc;
                        },
                        [] as any[],
                      )}
                    </FlexWrapper>
                  </div>
                )}
              <div>
                <SecondaryText style={{ marginBottom: 'var(--spacing-03)' }}>
                  <FormattedMessage id="settings.integrations.sf.enable_error_email.add_emails" />
                </SecondaryText>
                <SelectWrapper
                  style={{ maxWidth: WIDTH, gap: 'var(--spacing-03)' }}
                >
                  <CreatableSelect<EmailRecipientOption, true>
                    variant="user"
                    isMulti
                    virtualized
                    isClearable
                    maxMenuHeight={400} // Explicitly set menu height (default is 300px)
                    maxValueContainerHeight={112}
                    options={userOptions}
                    value={selectedUsers}
                    formatCreateLabel={() => (
                      <FormattedMessage id="invoices.email_editor.to_users.placeholder" />
                    )}
                    inputValue={userInput}
                    onInputChange={handleInputChange}
                    onBlur={(e) => handleAddEmailFlow(e.target.value)}
                    onCreateOption={(emailId) => handleAddEmailFlow(emailId)}
                    onChange={(newValue) => setSelectedUsers([...newValue])}
                    isValidNewOption={(input) => {
                      return isValidEmail(input.trim());
                    }}
                    closeMenuOnSelect={false}
                    className="flex-1"
                  />
                  <Button
                    variant="primary"
                    onClick={() => {
                      if (config?.settingsMeta) {
                        onUpdate({
                          ...config?.settingsMeta,
                          errorEmailRecipients: [
                            ...(config?.settingsMeta.errorEmailRecipients ??
                              []),
                            ...getUsersInBEFormat(selectedUsers),
                          ],
                        });
                      }
                    }}
                  >
                    {translate('add')}
                  </Button>
                </SelectWrapper>
                <SecondaryText style={{ marginTop: 'var(--spacing-02)' }}>
                  <FormattedMessage id="settings.integrations.sf.enable_error_email.add_emails.helper" />
                </SecondaryText>
              </div>
              <div>
                <SecondaryText style={{ marginBottom: 'var(--spacing-03)' }}>
                  <FormattedMessage id="settings.integrations.sf.enable_error_email.set_duration" />
                </SecondaryText>
                <Input
                  style={{ width: WIDTH }}
                  type="number"
                  value={timeDuration}
                  min={MIN}
                  suffix={translate(
                    'settings.integrations.sf.enable_error_email.emails.input_duration.suffix',
                  )}
                  onChange={(e) =>
                    setTimeDuration(e.target.value as unknown as number)
                  }
                  onBlur={resettingDurationIfValueIncorrect}
                />
              </div>
            </>
          )}
        </Space>
      </Container>
    </MaskableContent>
  );
};
